import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { acquireAccessToken } from 'app/msal-utils';
import { SESSION_STORAGE_KEYS, HEADER_NAMES } from 'app/constants';
import config from 'app/config';

const baseQuery = fetchBaseQuery({
  baseUrl: config.API_BASE_URL,
  prepareHeaders: (headers) => {
    return acquireAccessToken().then((token) => {
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      const activeRole = sessionStorage.getItem(SESSION_STORAGE_KEYS.ACTIVE_ROLE);
      if (activeRole) {
        headers.set(HEADER_NAMES.ACTIVE_ROLE, activeRole);
      }
      return headers;
    });
  },
});

// A cached ID token can look valid to MSAL (see app/msal-token.js's near-expiry refresh, which
// still leaves a gap up to an hour into a session) yet already be rejected by the backend as
// expired. On a 401, force a refresh - so prepareHeaders picks up a new token from MSAL's cache on
// the retry - and retry the request exactly once before surfacing the 401 to the caller.
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    try {
      // Captured *before* forcing the refresh so it reflects the token prepareHeaders actually
      // attached to the just-failed request. MSAL's own acquireTokenSilent request-deduplication
      // does not key on forceRefresh (see app/msal-token.js's own comment on the same MSAL
      // quirk in reverse), so a forced refresh that lands while an ordinary silent call for the
      // same account/scopes is already in flight can resolve to that same, already-rejected
      // token - retrying with it would just 401 again, so skip straight back to the original
      // failure instead.
      const rejectedToken = await acquireAccessToken();
      const refreshedToken = await acquireAccessToken({ forceRefresh: true });
      if (refreshedToken && refreshedToken !== rejectedToken) {
        result = await baseQuery(args, api, extraOptions);
      }
    } catch (refreshError) {
      // Keep the original 401 - app/msal-token.js's own forced-refresh handling already resolves
      // the auth flow itself (a throttled interactive redirect) rather than leaving this hanging.
    }
  }

  return result;
};

export const secureLubaApi = createApi({
  reducerPath: 'secureLubaApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Application', 'ApplicationLogs', 'ApplicationUnusedAirports', 'Permit', 'Trainings', 'GuestCheckpoints', 'BlackList', 'ElectronicKeys', 'Statistics', 'Me', 'OrgApplication', 'OrgPermit', 'OrgStatus', 'OrgChain', 'OrgPeople', 'Settings', 'Whitelist', 'OrgSync'],
  endpoints: (builder) => ({}),
});

export const {} = secureLubaApi;
